import { BlockBlobClient } from '@azure/storage-blob';
import { v4 as uuidv4 } from 'uuid'; // To generate a new GUID
import { useLazyGetSASForDeleteFileQuery, useLazyGetSASForReadFileQuery, useLazyGetSASForUploadFileQuery } from '../../../app/redux-fetch/apiCoreQuery';

export const useDocumentHandle = () => {

  const [getReadSas] = useLazyGetSASForReadFileQuery()
  const [getUploadSas] = useLazyGetSASForUploadFileQuery()
  const [getDeleteSas] = useLazyGetSASForDeleteFileQuery()

  // Helper function to parse SAS URL safely
  const parseSasUrl = (sasData: string | undefined, fileId?: string): string | null => {
    if (!sasData) {
      console.error('SAS token is undefined or empty');
      return null;
    }

    try {
      const parts = sasData.split('?');
      if (parts.length !== 2) {
        console.error('Invalid SAS token format');
        return null;
      }

      if (fileId) {
        return `${parts[0]}/${fileId}?${parts[1]}`;
      }

      return sasData;
    } catch (error) {
      console.error('Error parsing SAS URL:', error);
      return null;
    }
  }

  return {
    uploadDocument: async (file: File, documentType: string) => {
      try {
        const res = await getUploadSas().unwrap()
        const sasUrl = parseSasUrl(res);

        if (!sasUrl) {
          throw new Error('Failed to get valid SAS token for upload');
        }

        const name = `${documentType}.${uuidv4()}.${file.name}`
        const fullSasUrl = `${sasUrl.split('?')[0]}/${name}?${sasUrl.split('?')[1]}`

        const blobClient = new BlockBlobClient(fullSasUrl)
        const blobOptions = { blobHTTPHeaders: { blobContentType: file.type } }

        await blobClient.uploadData(file, blobOptions)
        return name
      } catch (error) {
        console.error('Error uploading to Azure Blob Storage:', error)
        throw error;
      }
    },
    viewDocument: async (fileId: string) => {
      if (!fileId) return;

      try {
        const res = await getReadSas().unwrap()
        const sasUrl = parseSasUrl(res, fileId);

        if (!sasUrl) {
          throw new Error('Failed to get valid SAS token for reading');
        }

        window.open(sasUrl, '_blank')
      } catch (error) {
        console.error('Error generating view URL:', error);
      }
    },
    removeDocument: async (fileId: string) => {
      if (!fileId) return;

      try {
        const res = await getDeleteSas().unwrap()
        const sasUrl = parseSasUrl(res, fileId);

        if (!sasUrl) {
          throw new Error('Failed to get valid SAS token for deletion');
        }

        const blobClient = new BlockBlobClient(sasUrl)
        await blobClient.delete()
        console.log(`Blob with name: ${fileId} deleted successfully from Azure Blob Storage.`)
        return true;
      } catch (error) {
        console.error('Error deleting from Azure Blob Storage:', error)
        throw error;
      }
    },
  }
}
